import React, { useRef, useState, useEffect } from 'react'
import { Button, FormItem, FormContainer, Alert } from 'components/ui'
import useTimeOutMessage from 'utils/hooks/useTimeOutMessage'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import useAuth from 'utils/hooks/useAuth'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch } from 'react-redux'
import {
    setAuthPanelLoginExpand,
    setAuthPanelSignupExpand,
} from 'store/theme/themeSlice'
import { apiResendOtp, apiSendOtp } from 'services/AuthService'
import { PasswordInput } from 'components/shared'
import { OtpInput } from 'reactjs-otp-input'

// Validation schema for the form
const validationSchema = Yup.object().shape({
    otp: Yup.string().required('Required').length(6),
    password: Yup.string().min(8).required('Please enter your password'),
    confirmPassword: Yup.string()
        .min(8)
        .oneOf([Yup.ref('password'), null], 'Your passwords do not match')
        .required('Please confirm your password'),
})

const ResetPasswordForm = (props) => {
    const { disableSubmit = false, className, email } = props
    const dispatch = useDispatch()

    const recaptchaRef = useRef(null)

    const { verifyOtp } = useAuth()
    const [isLoading, setIsLoading] = useState(false)
    const [otpSent, setOtpSent] = useState(true)
    const [successMessage, setSuccessMessage] = useTimeOutMessage(10000)
    const [message, setMessage] = useTimeOutMessage(10000)
    const [timer, setTimer] = useState(60)
    const [isCounting, setIsCounting] = useState(true)
    const [otpFieldDisabled, setOtpFieldDisabled] = useState(false)

    // Timer logic for OTP resend
    useEffect(() => {
        let interval
        if (isCounting && timer > 0) {
            interval = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1)
            }, 1000)
        } else if (timer === 0) {
            setIsCounting(false)
            setOtpFieldDisabled(false)
        }
        return () => clearInterval(interval)
    }, [isCounting, timer])

    // Handle OTP verification
    const onVerifyOtp = async (values, setSubmitting, setErrors) => {
        setSubmitting(true)
        const { otp, password, confirmPassword } = values
        const recaptchaToken = await recaptchaRef.current.executeAsync()

        const result = await verifyOtp({
            email,
            otp,
            password,
            password_confirmation: confirmPassword,
            recaptchaToken,
        })

        if (result.status === 'failed') {
            setMessage(result.message)
            result?.errors && setErrors(result.errors)
        }

        setSubmitting(false)
    }

    // Handle OTP resend
    const onResendOtp = async (setSubmitting, setErrors, resetForm) => {
        setIsLoading(true)

        const recaptchaToken = await recaptchaRef.current.executeAsync()

        const result = await apiResendOtp({
            email,
            recaptchaToken,
            isForgotPasswordRequest: true,
        })

        if (result.status === 'failed') {
            setMessage(result.message)
            result?.errors && setErrors(result.errors)
        } else {
            setOtpSent(true)
            setSuccessMessage('OTP has been sent to your email')
            setTimer(60)
            setIsCounting(true)
            setOtpFieldDisabled(true)
            resetForm()
        }

        setIsLoading(false)
    }

    return (
        <div className={className}>
            {successMessage && (
                <Alert className="mb-4" type="success" showIcon>
                    {successMessage}
                </Alert>
            )}

            {message && (
                <Alert className="mb-4" type="danger" showIcon>
                    {message}
                </Alert>
            )}

            <Formik
                initialValues={{
                    otp: '',
                    password: '',
                    confirmPassword: '',
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting, setErrors, resetForm }) => {
                    if (!disableSubmit) {
                        if (otpSent) {
                            onVerifyOtp(values, setSubmitting, setErrors)
                        } else {
                            onResendOtp(setSubmitting, setErrors, resetForm)
                        }
                    } else {
                        setSubmitting(false)
                    }
                }}
            >
                {({
                    touched,
                    errors,
                    isSubmitting,
                    setFieldValue,
                    values,
                    submitForm,
                    resetForm,
                }) => (
                    <Form>
                        <FormContainer className="text-center">
                            <div className="flex justify-center space-x-4 py-10">
                                <OtpInput
                                    value={values.otp}
                                    onChange={(otp) => {
                                        setFieldValue('otp', otp)
                                        if (otp.length === 6) {
                                            setTimeout(() => submitForm(), 0) // Ensure form is submitted after Formik state is updated
                                        }
                                    }}
                                    numInputs={6}
                                    isInputNum
                                    shouldAutoFocus
                                    containerStyle="flex justify-center space-x-6"
                                    inputStyle={{
                                        width: '3rem',
                                        height: '3rem',
                                        fontSize: '2rem',
                                        textAlign: 'center',
                                        borderBottom: '2px solid #B0B0B0',
                                        outline: 'none',
                                        transition: 'border-bottom-color 0.3s',
                                    }}
                                    focusStyle={{
                                        borderBottomColor: '#FF0000',
                                    }}
                                    hasErrored={!!errors.otp && touched.otp}
                                />
                            </div>
                            <FormItem
                                label="New Password"
                                invalid={errors.password && touched.password}
                                errorMessage={errors.password}
                            >
                                <Field
                                    autoComplete="off"
                                    name="password"
                                    placeholder="Password"
                                    component={PasswordInput}
                                />
                            </FormItem>
                            <FormItem
                                label="Confirm New Password"
                                invalid={
                                    errors.confirmPassword &&
                                    touched.confirmPassword
                                }
                                errorMessage={errors.confirmPassword}
                            >
                                <Field
                                    autoComplete="off"
                                    name="confirmPassword"
                                    placeholder="Confirm Password"
                                    component={PasswordInput}
                                />
                            </FormItem>
                            <div className="hidden">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="invisible"
                                    sitekey="6LfHNskUAAAAALXWeqW6kUjlmcU_r6zHHTHXLRay"
                                />
                            </div>
                            <Button
                                block
                                loading={isSubmitting}
                                variant="solid"
                                type="submit"
                                className="mt-6"
                            >
                                {isSubmitting
                                    ? otpSent
                                        ? 'Verifying...'
                                        : 'Sending...'
                                    : otpSent
                                    ? 'Update Password'
                                    : 'Send OTP'}
                            </Button>
                            {otpSent && (
                                <div className="mt-4 text-center">
                                    {isCounting ? (
                                        <span>
                                            Resend OTP in {timer} seconds
                                        </span>
                                    ) : (
                                        <Button
                                            loading={isLoading}
                                            block
                                            type="button"
                                            onClick={() => {
                                                onResendOtp(
                                                    () => {},
                                                    () => {},
                                                    resetForm
                                                )
                                            }}
                                        >
                                            Resend OTP
                                        </Button>
                                    )}
                                </div>
                            )}
                        </FormContainer>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

export default ResetPasswordForm
